import { CountryViewModel, EmploymentViewModelNew } from '@fora-monorepo/employer-my-pages-common/types';
import { Box, Table, Text } from '@foradevelopment/fora-ui';

import { useTypedTranslation } from '../../../i18n/i18n';
import { links } from '../../../router/links';
import { getEmploymentBoardDecisionLocks } from '../../../redux/employment/employmentThunks';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { formatEmployeeId, getEmployeeIdTypeString } from '../../../common/utils';
import { EmploymentTableRowExpandedContent, EmploymentTableRowIconCell } from '../../../components';
import { EmploymentsTableMarkupContent } from './EmploymentsTableMarkupContent';

type EmploymentsTableRowProps = {
  employment: EmploymentViewModelNew;
  countries: CountryViewModel[];
  mobileLayout: boolean;
  showIconColumn: boolean;
};

export const EmploymentsTableRow = ({
  employment,
  countries,
  mobileLayout,
  showIconColumn,
}: EmploymentsTableRowProps) => {
  const { t } = useTypedTranslation();
  const dispatch = useAppDispatch();
  const getHasBoardDecisionLock = async () => {
    const result = await dispatch(getEmploymentBoardDecisionLocks(employment.policyNumber));
    if (getEmploymentBoardDecisionLocks.fulfilled.match(result)) {
      const lockedBoardDecisionLock = result.payload.find(lock => lock.isLocked);
      return lockedBoardDecisionLock ?? null;
    }
    return null;
  };
  const payMonthMargin = useAppSelector(state => state.foraGlobal.settings.payMonthMargin);

  const markupExpiringText = employment.activeMarkup?.fokIsExpiring
    ? t('warningMessages.markupIsExpiringChangeText', { endDate: employment.activeMarkup?.fokIsExpiring })
    : undefined;

  const ExpandedContent = () => (
    <EmploymentTableRowExpandedContent
      employment={employment}
      getHasBoardDecisionLock={getHasBoardDecisionLock}
      countries={countries}
      formattedEmployeeId={formatEmployeeId(employment.employee.employeeId)}
      localizedIdTypeString={getEmployeeIdTypeString(employment.employee.identificationType)}
      editUrl={`${links.editEmployment}/${employment.policyNumber}`}
      markupExpiringText={markupExpiringText}
    />
  );
  if (mobileLayout) {
    return (
      <Table.Row
        customRowColor={employment.isTerminated ? 'black10' : undefined}
        testId={`employments-table-row-${employment.employee.employeeId}`}
      >
        {showIconColumn && (
          <EmploymentTableRowIconCell employment={employment} payMonthMargin={payMonthMargin} stopPropagation />
        )}
        <Table.Cell>
          <Text whiteSpace="nowrap" size="small">
            {formatEmployeeId(employment.employee.employeeId)}
          </Text>
        </Table.Cell>
        <Table.Cell>
          <Text size="small">
            {employment.employee.firstName} {employment.employee.lastName}
          </Text>
        </Table.Cell>
        <Box key="body">
          <ExpandedContent />
        </Box>
      </Table.Row>
    );
  }

  return (
    <Table.Row
      customRowColor={employment.isTerminated ? 'black10' : undefined}
      testId={`employments-table-row-${employment.employee.employeeId}`}
    >
      {showIconColumn && <EmploymentTableRowIconCell employment={employment} payMonthMargin={payMonthMargin} />}
      <Table.Cell>
        <Text whiteSpace="nowrap" size="small">
          {formatEmployeeId(employment.employee.employeeId)}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <Text size="small">{employment.employee.firstName}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text size="small">{employment.employee.lastName}</Text>
      </Table.Cell>
      <Table.Cell>
        <EmploymentsTableMarkupContent employment={employment} />
      </Table.Cell>
      <Box key="body">
        <ExpandedContent />
      </Box>
    </Table.Row>
  );
};
